import React, { useState, useEffect } from 'react';
import {
  Users, Activity, Zap, Shield, GraduationCap, ArrowRight, AlertTriangle, CheckCircle2, Target, ShieldAlert,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import EngineWeakLinkCallout from '../engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../engines/EngineActionPlanCard';
import { WeakestMeasureTile } from '../health/HealthActionSpine';
import PeerBenchmarkTrend from '../common/PeerBenchmarkTrend';
import api from '../../lib/api';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const barColor = (s) => (s >= 75 ? '#06b6d4' : s >= 60 ? '#14b8a6' : s >= 45 ? '#f59e0b' : '#f43f5e');
const barColorLight = (s) => (s >= 75 ? '#0e7490' : s >= 60 ? '#0f766e' : s >= 45 ? '#b45309' : '#be123c');
const band = (s) => (s >= 75 ? 'Strong' : s >= 60 ? 'Good' : s >= 45 ? 'Developing' : 'At-Risk');
const scoreText = (s) => (s >= 75 ? 'text-cyan-300' : s >= 60 ? 'text-teal-300' : s >= 45 ? 'text-amber-300' : 'text-rose-300');
const bandChip = (s) => (
  s >= 75 ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
    : s >= 60 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
      : s >= 45 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
);
const COMP = {
  capability: { label: 'Capability', icon: Activity },
  motivation: { label: 'Motivation', icon: Zap },
  retention: { label: 'Retention', icon: Shield },
  lei: { label: 'Learning Effectiveness', icon: GraduationCap },
};

export function DarkCard({ className = '', children, testId }) {
  return <div className={cn('rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl', className)} data-testid={testId}>{children}</div>;
}

function ScoreGauge({ score, size = 80 }) {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2a44" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={barColor(score)} strokeWidth="8"
          strokeDasharray={`${Math.max(0, Math.min(100, score)) * 2.64} 264`} strokeLinecap="round" />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center text-lg font-bold', scoreText(score))}>{Math.round(score)}</div>
    </div>
  );
}

export default function PeLeanOverview({ current, delta, valueAtStake, selectedOrg, selectedCohort, navigate }) {
  const [inv, setInv] = useState(null);
  useEffect(() => {
    if (!selectedOrg) { setInv(null); return; }
    let cancelled = false;
    api.get(`/capability-inventory/summary?organization_id=${encodeURIComponent(selectedOrg)}`)
      .then((r) => { if (!cancelled) setInv(r.data); }).catch(() => { if (!cancelled) setInv(null); });
    return () => { cancelled = true; };
  }, [selectedOrg]);
  const peScore = Math.round((current?.pe || 0) * 100);
  const components = ['capability', 'motivation', 'retention', 'lei']
    .filter((k) => typeof current?.[k] === 'number')
    .map((k) => ({ key: k, label: COMP[k].label, icon: COMP[k].icon, score: (current[k] || 0) * 100 }));
  const ranked = [...components].sort((a, b) => b.score - a.score);
  const strongest = ranked[0] || null;
  const weak = components.filter((c) => c.score > 0).reduce((lo, c) => (lo == null || c.score < lo.score ? c : lo), null);
  const LIFT_POINTS = 15;
  const realizedPct = weak ? Math.max(0, Math.min(100, weak.score)) : 0;
  const afterFixPct = Math.min(100, realizedPct + LIFT_POINTS);
  const deltaPct = afterFixPct - realizedPct;
  const vas = valueAtStake?.annual_value_at_stake_usd ?? null;
  const currency = valueAtStake?.currency || 'USD';
  const financialImpact = weak ? {
    realizedPct, afterFixPct, deltaPct, currency,
    liftLabel: `Lift ${weak.label} +${LIFT_POINTS}pts`,
    dollarsLeaking: vas != null ? vas * (1 - realizedPct / 100) : null,
    dollarsUnlocked: vas != null ? vas * (deltaPct / 100) : null,
    leakCopy: `Every quarter you leave ${weak.label} at ${Math.round(realizedPct)}/100, roughly ${Math.round(100 - realizedPct)}% of every learning dollar leaks before it reaches the business.`,
    fixCopy: `Closing the ${weak.label} gap by ${LIFT_POINTS}pts shifts your realized L&D ROI by ${Math.round(deltaPct)} percentage points.`,
    vasFootnote: valueAtStake ? { revenue: valueAtStake.inputs?.annual_revenue, marginPct: valueAtStake.inputs?.operating_margin_pct, isDefault: !!valueAtStake.is_default } : null,
  } : undefined;

  const peFocus = weak ? {
    engine: 'pe',
    entityId: weak.key,
    entityLabel: weak.label,
    pillarId: weak.key,
    score: Math.round(weak.score * 10) / 10,
    scoreLabel: `${Math.round(weak.score)} / 100`,
    target: 100,
    gapPct: Math.max(0, 100 - Math.round(weak.score)),
    lift: LIFT_POINTS,
    overallScore: peScore,
    granularity: 'component',
    granularityLabel: 'component',
    organizationId: selectedOrg && selectedOrg !== 'all' ? selectedOrg : undefined,
  } : null;

  // Alerts & Risks — components below the healthy band (60), plus any declining
  // component (delta ≤ −0.05 on the 0–1 scale). Ordered high → medium → info.
  const alerts = [];
  components.forEach((c) => {
    if (c.score < 45) {
      alerts.push({ id: `low-${c.key}`, component: c.key, severity: 'high', title: `${c.label} is At-Risk (${Math.round(c.score)}/100)`,
        detail: `Below the 45 floor — this is dragging PE down the most. Prioritise it in the action plan.` });
    } else if (c.score < 60) {
      alerts.push({ id: `dev-${c.key}`, component: c.key, severity: 'medium', title: `${c.label} is only Developing (${Math.round(c.score)}/100)`,
        detail: `Under the 60 healthy band — a targeted lift here compounds across the composite PE.` });
    }
  });
  components.forEach((c) => {
    const d = delta?.[c.key];
    if (typeof d === 'number' && d <= -0.05 && !alerts.some((a) => a.component === c.key)) {
      alerts.push({ id: `drop-${c.key}`, component: c.key, severity: 'info', title: `${c.label} slipped ${(d * 100).toFixed(1)} pts`,
        detail: 'Declined versus the previous period — watch for a developing trend.' });
    }
  });
  const sevRank = { high: 0, medium: 1, info: 2 };
  alerts.sort((a, b) => sevRank[a.severity] - sevRank[b.severity]);

  // Peer benchmark for the PE composite (like Learning Health).
  const bench = current?.peer_benchmark || null;
  const benchClamp = (v) => Math.max(2, Math.min(98, v || 0));
  const benchLabelChip = bench && /strong|above|leader/i.test(bench.performance_label || '')
    ? 'bg-emerald-500/15 text-emerald-300'
    : bench && /below|risk|lag/i.test(bench.performance_label || '')
      ? 'bg-rose-500/15 text-rose-300'
      : 'bg-slate-500/20 text-slate-300';

  return (
    <div className="space-y-4" data-testid="pe-overview">
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="pe-kpi-tiles">
        <DarkCard className="p-5" testId="pe-kpi-score">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">PE Score</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className={cn('text-4xl font-heading font-extrabold tabular-nums', scoreText(peScore))}>{peScore}</span>
                <span className="text-sm text-slate-500 font-semibold">/100</span>
              </div>
              <span className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', bandChip(peScore))}>{band(peScore)}</span>
            </div>
            <ScoreGauge score={peScore} />
          </div>
        </DarkCard>

        <DarkCard className="p-5 relative overflow-hidden ring-1 ring-rose-500/40 mc-priority-glass shadow-lg shadow-rose-900/20" testId="pe-kpi-priority">
          <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300"><AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Top Priority</div>
          <p className="mt-2 text-2xl font-extrabold text-white leading-tight">{weak ? weak.label : '—'}</p>
          {weak && <p className="mt-1 text-sm text-rose-300 font-semibold tabular-nums">Score {Math.round(weak.score)}<span className="text-slate-400 font-normal"> / 100</span></p>}
          {weak && (
            <button onClick={() => navigate('/people-engine/deep-dives', { state: { focusComponent: weak.key } })} data-testid="pe-kpi-priority-action"
              className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
              <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </DarkCard>

        {components.slice(0, 3).map((c) => (
          <DarkCard className="p-5" testId={`pe-kpi-${c.key}`} key={c.key}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><c.icon className="h-3.5 w-3.5 text-[#44b6d2]" /> {c.label}</div>
            <p className={cn('mt-2 text-3xl font-heading font-extrabold tabular-nums', scoreText(c.score))}>{Math.round(c.score)}%</p>
            {typeof delta?.[c.key] === 'number' && Math.abs(delta[c.key]) >= 0.05 && (
              <p className={cn('text-[11px] font-bold mt-1', delta[c.key] > 0 ? 'text-emerald-400' : 'text-rose-400')}>{delta[c.key] > 0 ? '▲ +' : '▼ '}{delta[c.key].toFixed(1)} pts vs previous</p>
            )}
          </DarkCard>
        ))}
      </div>

      {peFocus && null}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        <DarkCard className="p-5 lg:col-span-7 h-full" testId="pe-component-breakdown">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Workforce Component Breakdown</div>
          <p className="text-xs text-slate-500 mt-1 mb-4">Capability × Motivation × Retention × Learning Effectiveness drive the composite PE</p>
          <div className="space-y-1">
            {ranked.map((c) => {
              const col = barColor(c.score);
              const isWeak = weak && c.key === weak.key;
              return (
                <div key={c.key} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl" data-testid={`pe-comp-row-${c.key}`}>
                  <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center"><c.icon className="h-4 w-4 text-[#44b6d2]" /></span>
                  <div className="min-w-0 w-40 sm:w-52 shrink-0">
                    <p className="text-sm font-semibold text-slate-100 truncate">{c.label}</p>
                    <p className="text-[11px] font-medium mc-band" style={{ color: col, '--bl': barColorLight(c.score) }}>{band(c.score)}</p>
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-[#0b1220] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: col }} /></div>
                  {isWeak && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Constraint</span>}
                  <span className="shrink-0 w-8 text-right text-lg font-bold tabular-nums mc-band" style={{ color: col, '--bl': barColorLight(c.score) }}>{Math.round(c.score)}</span>
                </div>
              );
            })}
          </div>
        </DarkCard>
        <DarkCard className="p-5 lg:col-span-5 h-full flex flex-col" testId="pe-inventory-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Capability Inventory</div>
            <button onClick={() => navigate('/people-engine/inventory')} className="text-[11px] font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="pe-inventory-summary-open">View <ArrowRight className="h-3 w-3" /></button>
          </div>
          {inv ? (
            <div className="grid grid-cols-2 gap-3 flex-1 auto-rows-fr" data-testid="pe-inventory-summary-tiles">
              <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500"><Target className="h-3.5 w-3.5 text-[#44b6d2]" /> Role Coverage</div>
                <p className={cn('mt-1.5 text-2xl font-extrabold tabular-nums', inv.role_coverage_pct >= 90 ? 'text-emerald-300' : inv.role_coverage_pct >= 60 ? 'text-amber-300' : 'text-rose-300')}>{inv.role_coverage_pct}%</p>
                <p className="text-[10px] text-slate-500">{inv.roles_fully_staffed}/{inv.total_roles} roles covered</p>
              </div>
              <button type="button" onClick={() => navigate('/people-engine/inventory?status=at_risk,critical')} data-testid="pe-inventory-roles-at-risk" className="text-left rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-3 hover:ring-rose-500/40 transition-colors">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500"><ShieldAlert className="h-3.5 w-3.5 text-rose-400" /> Roles at Risk</div>
                <p className={cn('mt-1.5 text-2xl font-extrabold tabular-nums', inv.roles_at_risk > 0 ? 'text-rose-300' : 'text-emerald-300')}>{inv.roles_at_risk}</p>
                <p className="text-[10px] text-[#44b6d2]">View at-risk roles →</p>
              </button>
              <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Deep Bench</div>
                <p className="mt-1.5 text-2xl font-extrabold tabular-nums text-slate-100">{Math.max(0, inv.total_roles - inv.roles_at_risk)}/{inv.total_roles}</p>
                <p className="text-[10px] text-slate-500">≥2 ready holders</p>
              </div>
              <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500"><Users className="h-3.5 w-3.5 text-[#44b6d2]" /> Members</div>
                <p className="mt-1.5 text-2xl font-extrabold tabular-nums text-slate-100">{inv.total_members}</p>
                <p className="text-[10px] text-slate-500">{inv.member_coverage_pct}% cover their role</p>
              </div>
            </div>
          ) : <p className="text-sm text-slate-500">No capability inventory data yet.</p>}
        </DarkCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DarkCard className="p-5 h-full flex flex-col" testId="pe-strongest-card">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Strongest Component</div>
            {strongest ? (
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-400" /></span>
                <div>
                  <p className="text-lg font-bold text-slate-100">{strongest.label}</p>
                  <p className="text-sm text-emerald-300 font-semibold tabular-nums">{Math.round(strongest.score)}/100</p>
                </div>
              </div>
            ) : <p className="text-sm text-slate-500">No component data.</p>}
            <button onClick={() => navigate('/people-engine/deep-dives')} className="mt-auto pt-4 text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="pe-open-diagnostic">See the action plan on Priorities <ArrowRight className="h-3.5 w-3.5" /></button>
          </DarkCard>

          <DarkCard className="p-5 h-full flex flex-col" testId="pe-peer-benchmark">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Peer Benchmark · PE</div>
            {bench ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold tabular-nums text-slate-100">{Math.round(bench.percentile)}<span className="text-base align-top">{ordinal(Math.round(bench.percentile))}</span></span>
                  <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', benchLabelChip)}>{bench.performance_label || 'Peer'}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5">percentile vs {bench.industry_name || 'peers'}</p>
                {/* percentile bar: p25 → p90 range with peer-median + your marker */}
                <div className="mt-4">
                  <div className="relative h-2 rounded-full bg-[#0b1220] ring-1 ring-[#22304d]">
                    <div className="absolute inset-y-0 rounded-full bg-cyan-500/25" style={{ left: `${benchClamp(bench.p25)}%`, right: `${100 - benchClamp(bench.p90)}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3.5 bg-slate-400" style={{ left: `${benchClamp(bench.peer_median)}%` }} title={`Peer median ${Math.round(bench.peer_median)}`} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#44b6d2] ring-2 ring-[#0b1220]" style={{ left: `calc(${benchClamp(peScore)}% - 6px)` }} title={`Your PE ${peScore}`} data-testid="pe-benchmark-marker" />
                  </div>
                  <div className="flex justify-between text-[9px] text-slate-500 mt-1.5">
                    <span>You {peScore}</span>
                    <span>Median {Math.round(bench.peer_median)}</span>
                  </div>
                </div>
                <p className="mt-auto pt-3 text-[10px] text-slate-500">n≈{bench.sample_size} orgs</p>
                <PeerBenchmarkTrend stage="capability" organizationId={selectedOrg} testId="pe-benchmark-trend" />
              </>
            ) : <p className="text-sm text-slate-500">No benchmark data.</p>}
          </DarkCard>
        </div>

        <DarkCard className="p-5 lg:col-span-5 h-full flex flex-col" testId="pe-overview-alerts">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400" /> Alerts &amp; Risks
          </div>
          {alerts.length ? (
            <div className="space-y-2.5" data-testid="pe-overview-alerts-list">
              {alerts.map((a) => (
                <button key={a.id} type="button"
                  onClick={() => navigate('/people-engine/deep-dives', { state: { focusComponent: a.component } })}
                  className={cn('w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 ring-1 transition-colors hover:ring-2',
                  a.severity === 'high' ? 'bg-rose-500/10 ring-rose-500/30 hover:ring-rose-400' : a.severity === 'medium' ? 'bg-amber-500/10 ring-amber-500/25 hover:ring-amber-400' : 'bg-slate-500/10 ring-slate-500/20 hover:ring-slate-400')}
                  data-testid={`pe-alert-${a.id}`}>
                  <span className={cn('shrink-0 mt-0.5 w-1.5 h-8 rounded-full', a.severity === 'high' ? 'bg-rose-500' : a.severity === 'medium' ? 'bg-amber-400' : 'bg-slate-400')} />
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm font-semibold', a.severity === 'high' ? 'text-rose-200' : a.severity === 'medium' ? 'text-amber-200' : 'text-slate-200')}>{a.title}</p>
                    <p className="text-[11px] text-slate-400 leading-snug mt-0.5">{a.detail}</p>
                    <span className="text-[10px] font-semibold text-[#44b6d2] inline-flex items-center gap-0.5 mt-1">Open action plan <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div data-testid="pe-overview-alerts-clear">
              <div className="flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle2 className="h-4 w-4" /> No components below the healthy band right now.
              </div>
              {ranked.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Watch list — lowest first</p>
                  <div className="space-y-2">
                    {[...ranked].reverse().slice(0, 3).map((c) => (
                      <div key={c.key} className="flex items-center gap-3" data-testid={`pe-watch-${c.key}`}>
                        <span className="min-w-0 w-28 shrink-0 text-xs font-semibold text-slate-300 truncate">{c.label}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-[#0b1220] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: barColor(c.score) }} /></div>
                        <span className="shrink-0 w-8 text-right text-xs font-bold tabular-nums text-slate-300">{Math.round(c.score)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <button onClick={() => navigate('/people-engine/deep-dives')} className="mt-auto pt-4 text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="pe-overview-alerts-open">
            View full risk breakdown <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </DarkCard>
      </div>
    </div>
  );
}
